/*!
 * Copyright © 2025. Verizon Connect Ireland Limited. All rights reserved.
 */

import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { isElementNull } from '../../../../utils';
import { DeviceHealthIssueCategory, EPlotType } from '../../../core/models';
import { IDeviceHealth } from '../../../core/store/state';

@Component({
  selector: 'fm-device-health-balloon-section',
  templateUrl: './device-health-balloon-section.component.html',
  styleUrls: ['./device-health-balloon-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceHealthBalloonSectionComponent implements OnChanges {
  @Input()
  public vehicleHealthStatus: IDeviceHealth;
  @Input()
  public hasPermissionForCameras: boolean;
  @Input()
  public hasPermissionToViewHealthStatus: boolean;
  @Input()
  public deviceType: EPlotType;

  public healthCategoryCss: string;
  public showComponentHealthMultipleError: boolean;
  public showCameraHealthMultipleError: boolean;
  public cameraTroubleshootLink: string;
  public trackerTroubleshootLink: string;

  constructor(@Inject(DOCUMENT) private readonly _document: Document) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.vehicleHealthStatus) {
      this.healthCategoryCss = this._getHealthStatusCss();
      this.showCameraHealthMultipleError = this._showMultipleCameraHealthError();
      this.showComponentHealthMultipleError = this._showMultipleComponentHealthError();
      this.cameraTroubleshootLink = this._getTroubleshootingLinkForCamera();
      this.trackerTroubleshootLink = this._getTroubleshootingLinkForTracker();
    }
  }

  onTrackerTroubleshoot() {
    this._document.defaultView.window.open(this.trackerTroubleshootLink);
  }

  onCameraTroubleshoot() {
    this._document.defaultView.window.open(this.cameraTroubleshootLink);
  }

  private _getHealthStatusCss(): string {
    return this.vehicleHealthStatus?.healthStatus.toUpperCase() == DeviceHealthIssueCategory.Critical
      ? 'device-health-status__container__health-header__status__critical'
      : this.vehicleHealthStatus?.healthStatus.toUpperCase() == DeviceHealthIssueCategory.Major
      ? 'device-health-status__container__health-header__status__major'
      : this.vehicleHealthStatus?.healthStatus.toUpperCase() == DeviceHealthIssueCategory.Moderate
      ? 'device-health-status__container__health-header__status__moderate'
      : '';
  }

  private _getTroubleshootingLinkForTracker(): string {
    let vehicleId: number = parseInt(this.vehicleHealthStatus?.vehicleId);
    return `/fleet/${this._encode({
      fleetId: vehicleId,
      fleetType: this._getHostTypeForTroubleshoot(this.vehicleHealthStatus?.hostType),
      deviceId: this.vehicleHealthStatus?.componentHealth[0]?.DeviceId
    })}/device-details`;
  }

  private _getHostTypeForTroubleshoot(hostType: string): string {
    if (isElementNull(hostType)) return '';
    if (hostType.toUpperCase() == 'POWERED ASSET') return 'PoweredAsset';
    if (hostType.toUpperCase() == 'NON-POWERED ASSET') return 'NonPoweredAsset';
    return hostType;
  }

  private _getTroubleshootingLinkForCamera(): string {
    let vehicleId: number = parseInt(this.vehicleHealthStatus?.vehicleId);
    return `/integratedvideo/cameras/${this._encode({
      vehicleId: vehicleId,
      controlUnitIds: this.vehicleHealthStatus?.cameraHealth.map(x => x.ControlUnitIds)[0]
    })}`;
  }

  private _showMultipleComponentHealthError(): boolean {
    return this.vehicleHealthStatus?.componentHealth.length > 1;
  }

  private _showMultipleCameraHealthError(): boolean {
    return this.vehicleHealthStatus?.cameraHealth.length > 1;
  }

  private _encode(data: Record<string, unknown>): string {
    let stringToEncode = JSON.stringify(data);
    return this._getBase64StringWithoutPadding(this._document.defaultView.btoa(stringToEncode));
  }

  private _getBase64StringWithoutPadding(url: string): string {
    if (url.endsWith('=')) {
      return this._getBase64StringWithoutPadding(url.slice(0, url.length - 1));
    }
    return url;
  }
}
